import React, { useState, useEffect, useRef } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';
import { BASE_URL } from '../constants';
import { useNavigate } from 'react-router-dom';
import FaceCaptureModal from './FaceCaptureModal';

export default function FaceRec({ onVerified, onClose }) {
  const videoRef = useRef(null);
  const [modelsLoaded, setModelsLoaded] = useState(false);
  const [referenceDescriptor, setReferenceDescriptor] = useState(null);
  const [result, setResult] = useState('Loading models...');
  const [verifying, setVerifying] = useState(false);
  const [showCaptureModal, setShowCaptureModal] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(true);
  const [loadingReference, setLoadingReference] = useState(true);

  const navigate = useNavigate();
  const userData = JSON.parse(localStorage.getItem('userData')) || {};
  const refugeeId = userData?.id;

  // Load face-api models
  useEffect(() => {
    async function loadModels() {
      const MODEL_URL = process.env.PUBLIC_URL + '/models';
      await Promise.all([
        faceapi.nets.ssdMobilenetv1.loadFromUri(MODEL_URL),
        faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        faceapi.nets.faceRecognitionNet.loadFromUri(MODEL_URL),
      ]);
      setModelsLoaded(true);
      setResult('Models loaded. Loading reference face...');
    }
    loadModels();
  }, []);

  // Load reference face
  useEffect(() => {
    if (!modelsLoaded) return;

    async function loadReferenceFace() {
      if (!refugeeId) {
        setResult('User not recognized.');
        setLoadingReference(false);
        return;
      }

      try {
        const response = await axios.get(`${BASE_URL}/refugees/${refugeeId}/face-image`, {
          responseType: 'blob',
          validateStatus: status => status < 500,
        });

        if (response.data.type === 'application/json' || response.data.size === 0) {
          setShowCaptureModal(true);
          setLoadingReference(false);
          return;
        }

        const imageURL = URL.createObjectURL(response.data);
        const img = await faceapi.fetchImage(imageURL);
        const detection = await faceapi
          .detectSingleFace(img)
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (!detection) {
          setShowCaptureModal(true);
          setLoadingReference(false);
          return;
        }

        setReferenceDescriptor(detection.descriptor);
        setResult('Reference face loaded. Starting verification...');
      } catch (error) {
        console.error('Error loading face image:', error);
        setShowCaptureModal(true);
      } finally {
        setLoadingReference(false);
      }
    }

    loadReferenceFace();
  }, [modelsLoaded, refugeeId]);

  // Start camera immediately
  useEffect(() => {
    let stream;
    if (showVerificationModal && videoRef.current) {
      (async () => {
        try {
          stream = await navigator.mediaDevices.getUserMedia({ video: true });
          videoRef.current.srcObject = stream;
        } catch (err) {
          setResult('Unable to access camera.');
          console.error(err);
        }
      })();
    }

    return () => {
      if (stream) stream.getTracks().forEach(track => track.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [showVerificationModal]);

  // Verification loop
  useEffect(() => {
    if (!showVerificationModal || !referenceDescriptor || loadingReference) return;

    setResult('Verifying...');
    setVerifying(true);

    const intervalId = setInterval(async () => {
      if (!videoRef.current) return;

      try {
        const detection = await faceapi
          .detectSingleFace(videoRef.current)
          .withFaceLandmarks()
          .withFaceDescriptor();

        if (!detection) {
          setResult('No face detected. Please try again.');
          return;
        }

        const distance = faceapi.euclideanDistance(referenceDescriptor, detection.descriptor);
        const match = distance < 0.5;

        if (match) {
          setResult('✅ Verification successful.');
          setVerifying(false);
          clearInterval(intervalId);
          setShowVerificationModal(false);
          if (onVerified) onVerified();
        } else {
          setResult('❌ Face does not match. Please try again.');
        }
      } catch (err) {
        console.error('Verification error:', err);
        setResult('Error during verification.');
        setVerifying(false);
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
  }, [showVerificationModal, referenceDescriptor, loadingReference, onVerified]);

  if (showCaptureModal) {
    return (
      <FaceCaptureModal
        onClose={() => {
          setShowCaptureModal(false);
          if (onClose) onClose();
        }}
        onCapture={() => {
          setShowCaptureModal(false);
          if (onClose) onClose();
          window.location.reload();
        }}
      />
    );
  }

  return (
    <>
      {showVerificationModal && (
        <div
          className="modal show d-block"
          tabIndex="-1"
          role="dialog"
          style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <div className="modal-dialog modal-dialog-centered" role="document">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Face Verification</h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={() => {
                    setShowVerificationModal(false);
                    setVerifying(false);
                    setResult('');
                    if (onClose) onClose();
                  }}
                />
              </div>
              <div className="modal-body text-center">
                <video
                  ref={videoRef}
                  autoPlay
                  muted
                  playsInline
                  width={320}
                  height={240}
                  style={{ borderRadius: 8, border: '1px solid black' }}
                />
                <p style={{ marginTop: 20, fontWeight: 'bold' }}>
                  {loadingReference ? 'Loading reference face...' : result}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
